/**
 * Drug-drug interaction mentions between a patient's medications, from the annotated FDA
 * labels in drugLabels (see functions/src/openfda/interactions.js for how a label's
 * `interactions` and `selfKeys` are built).
 *
 * Two medications are flagged when either one's label names the other — its ingredient or one
 * of its drug classes — in a safety section. What comes back is what the labels SAY, with the
 * sentence, not a clinical verdict; render it that way.
 */

import { isBefore, isValid, parseISO, startOfToday } from "date-fns";

export const SEVERITY_RANK = { avoid: 3, warning: 2, caution: 1 };

/**
 * A medication whose recorded course has already ended interacts with nothing now: one with a
 * past end date, or one the patient told the assistant they stopped (status "stopped", which
 * the assistant records without an end date when none was given).
 */
export function isCurrentMedication(medication) {
  if (medication?.status === "stopped") return false;
  if (!medication?.endDate) return true;
  const end = parseISO(String(medication.endDate));
  return !isValid(end) || !isBefore(end, startOfToday());
}

/** What `from`'s label says about `to`'s ingredients and classes. */
function mentions(from, to) {
  const target = new Set(to.label.selfKeys || []);
  return (from.label.interactions || [])
    .filter((i) => target.has(i.key))
    .map((i) => ({
      fromName: from.medication.name,
      aboutName: to.medication.name,
      matched: i.name,
      severity: i.severity,
      section: i.section,
      excerpt: i.excerpt,
      labelUrl: from.label.dailymedUrl || null,
    }));
}

/**
 * `entries`: `[{ medication, label }]`, label being an annotated drugLabels document (entries
 * without one are skipped). Returns one item per interacting pair, most severe first:
 * `{ id, medications: [a, b], severity, mentions }`. A label naming both a drug and its class
 * in the same sentence yields that sentence once.
 */
export function findMedicationInteractions(entries) {
  const usable = (entries || []).filter((e) => e?.label?.selfKeys && e.medication);
  const pairs = [];

  for (let i = 0; i < usable.length; i++) {
    for (let j = i + 1; j < usable.length; j++) {
      const a = usable[i];
      const b = usable[j];
      // The same product entered twice is a duplicate, not an interaction.
      if (a.label.setId && a.label.setId === b.label.setId) continue;

      const unique = new Map();
      for (const m of [...mentions(a, b), ...mentions(b, a)]) {
        const key = `${m.fromName}|${m.excerpt}`;
        const prev = unique.get(key);
        if (!prev || SEVERITY_RANK[m.severity] > SEVERITY_RANK[prev.severity]) unique.set(key, m);
      }
      if (!unique.size) continue;

      const found = [...unique.values()].sort(
        (x, y) => SEVERITY_RANK[y.severity] - SEVERITY_RANK[x.severity]
      );
      pairs.push({
        id: `${a.medication.id || a.medication.name}|${b.medication.id || b.medication.name}`,
        medications: [a.medication, b.medication],
        severity: found[0].severity,
        mentions: found,
      });
    }
  }

  return pairs.sort((x, y) => SEVERITY_RANK[y.severity] - SEVERITY_RANK[x.severity]);
}
